import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as InfoIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  VisibilityOutlined as ViewIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  ndaService,
  researchProjectService,
  ndaTypeService,
  buildFileUrl,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

const NDAManagement = () => {
  const { user, isAdmin, isInvestigador } = useAuth();
  const [ndas, setNdas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ndaTypes, setNdaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [editingNda, setEditingNda] = useState(null);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedNda, setSelectedNda] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [registeringNdaId, setRegisteringNdaId] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ndaTypeId: "",
  });

  const loadNdas = async () => {
    try {
      setLoading(true);
      const response = await ndaService.getAll();
      setNdas(response.data);
    } catch (err) {
      setError("Error al cargar NDAs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = useCallback(async () => {
    try {
      const response = await researchProjectService.getAll();
      let allProjects = response.data || [];

      // Si es investigador, solo mostrar sus proyectos que requieren NDA y que aún no tienen NDA
      if (isInvestigador() && !isAdmin()) {
        // Obtener todos los NDAs para verificar cuáles proyectos ya tienen NDA
        const ndasResponse = await ndaService.getAll();
        const ndasList = ndasResponse.data || [];
        const projectsWithNda = new Set(
          ndasList.map((nda) => nda.researchProjectId)
        );

        // Filtrar: solo proyectos del usuario, que requieren NDA, y que aún no tienen NDA
        allProjects = allProjects.filter(
          (project) =>
            project.userId === user.id &&
            project.requiresNDA === true &&
            !projectsWithNda.has(project.id)
        );
      }

      setProjects(allProjects);
    } catch (err) {
      console.error("Error al cargar proyectos:", err);
    }
  }, [user, isInvestigador, isAdmin]);

  useEffect(() => {
    loadNdas();
    loadProjects();
    loadNdaTypes();
  }, [loadProjects]);

  const loadNdaTypes = async () => {
    try {
      const response = await ndaTypeService.getAll({ isActive: true });
      setNdaTypes(response.data.data || []);
    } catch (err) {
      console.error("Error al cargar tipos de NDA:", err);
    }
  };

  const handleOpenDialog = (nda = null) => {
    if (nda) {
      setEditingNda(nda);
      setFormData({
        title: nda.title,
        description: nda.description || "",
        ndaTypeId: nda.ndaTypeId || "",
      });
      setSelectedFile(null);
    } else {
      setEditingNda(null);
      setFormData({
        title: "",
        description: "",
        ndaTypeId: "",
      });
      setSelectedFile(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNda(null);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("ndaTypeId", formData.ndaTypeId);
      // Agregar archivo si se seleccionó uno
      if (selectedFile) {
        formDataToSend.append("file", selectedFile);
      }

      if (editingNda) {
        await ndaService.update(editingNda.id, formDataToSend);
        setSuccess("NDA actualizado exitosamente");
      } else {
        await ndaService.create(formDataToSend);
        setSuccess("NDA creado exitosamente");
      }

      setTimeout(() => {
        handleCloseDialog();
        loadNdas();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar NDA");
    }
  };

  const handleRegisterBlockchain = async (ndaId) => {
    if (registeringNdaId) return; // Evitar múltiples clics

    try {
      setRegisteringNdaId(ndaId);
      setError("");
      setSuccess("");
      await ndaService.registerBlockchain(ndaId);
      setSuccess("NDA registrado en blockchain exitosamente");
      await loadNdas();
    } catch (err) {
      setError(
        err.response?.data?.error || "Error al registrar NDA en blockchain"
      );
    } finally {
      setRegisteringNdaId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este NDA?")) return;

    try {
      await ndaService.delete(id);
      setSuccess("NDA eliminado exitosamente");
      loadNdas();
    } catch (err) {
      setError("Error al eliminar NDA");
    }
  };

  const handleShowInfo = (nda) => {
    setSelectedNda(nda);
    setOpenInfoDialog(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" component="h1">
              Gestión de NDAs
            </Typography>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuevo NDA
            </Button>
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* NDAs Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="center">Aprobación Legal</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ndas.map((nda) => (
                <TableRow key={nda.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2">{nda.title}</Typography>
                      {nda.isGeneric && (
                        <Chip label="Genérico" color="info" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {nda.NDAType?.title || "Sin tipo"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {nda.description || "Sin descripción"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {nda.approvalStatus === "approved" ? (
                      <Chip label="Aprobado" color="success" size="small" />
                    ) : nda.approvalStatus === "rejected" ? (
                      <Chip label="Rechazado" color="error" size="small" />
                    ) : (
                      <Chip label="Pendiente" color="warning" size="small" />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleShowInfo(nda)}
                      title="Ver información"
                      sx={{ color: "#2196f3" }} // Azul
                    >
                      <InfoIcon />
                    </IconButton>
                    {!nda.blockchainTxHash &&
                      (isInvestigador() || isAdmin()) && (
                        <IconButton
                          size="small"
                          onClick={() => handleRegisterBlockchain(nda.id)}
                          title="Registrar en blockchain"
                          sx={{ color: "#9c27b0" }} // Violeta
                          disabled={
                            registeringNdaId === nda.id || !!registeringNdaId
                          }
                        >
                          {registeringNdaId === nda.id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <UploadIcon />
                          )}
                        </IconButton>
                      )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(nda)}
                      title="Editar"
                      sx={{ color: "#2e7d32" }} // Verde oscuro
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(nda.id)}
                      title="Eliminar"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {ndas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay NDAs registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editingNda ? "Editar NDA" : "Nuevo NDA"}</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                name="title"
                label="Título del NDA"
                value={formData.title}
                onChange={handleInputChange}
                required
              />

              <TextField
                fullWidth
                margin="normal"
                name="description"
                label="Descripción del NDA"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                helperText="Descripción del acuerdo de confidencialidad"
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo de NDA</InputLabel>
                <Select
                  name="ndaTypeId"
                  value={formData.ndaTypeId}
                  onChange={handleInputChange}
                  label="Tipo de NDA"
                >
                  <MenuItem value="">Seleccione un tipo de NDA</MenuItem>
                  {ndaTypes.map((ndaType) => (
                    <MenuItem key={ndaType.id} value={ndaType.id}>
                      {ndaType.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2, mb: 2 }}>
                <input
                  accept=".doc,.docx"
                  style={{ display: "none" }}
                  id="nda-file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="nda-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<UploadIcon />}
                  >
                    {selectedFile
                      ? `Archivo seleccionado: ${selectedFile.name}`
                      : editingNda?.filePath
                      ? "Reemplazar archivo (opcional)"
                      : "Subir archivo del NDA (opcional)"}
                  </Button>
                </label>
                {selectedFile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Archivo: {selectedFile.name} (
                    {(selectedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
                {editingNda?.filePath && !selectedFile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Archivo actual: {editingNda.filePath.split("/").pop()}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingNda ? "Actualizar" : "Crear"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Info Dialog */}
        <Dialog
          open={openInfoDialog}
          onClose={() => setOpenInfoDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Información del NDA</DialogTitle>
          <DialogContent>
            {selectedNda && (
              <Box sx={{ mt: 2 }}>
                {/* Título */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Título:
                  </Typography>
                  <Typography variant="h6">{selectedNda.title}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Tipo de NDA */}
                {selectedNda.NDAType && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Tipo de NDA:
                    </Typography>
                    <Typography variant="body2">
                      {selectedNda.NDAType.title}
                    </Typography>
                  </Box>
                )}

                {/* Estado de Aprobación Legal */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Aprobación Legal:
                  </Typography>
                  {selectedNda.approvalStatus === "approved" ? (
                    <Chip label="Aprobado por Estudio Jurídico" color="success" size="small" />
                  ) : selectedNda.approvalStatus === "rejected" ? (
                    <Chip label="Rechazado por Estudio Jurídico" color="error" size="small" />
                  ) : (
                    <Chip label="Pendiente de aprobación legal" color="warning" size="small" />
                  )}
                  {selectedNda.approvalComments && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                      Comentarios: {selectedNda.approvalComments}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Descripción */}
                {selectedNda.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Descripción:
                    </Typography>
                    <Typography
                      variant="body2"
                      paragraph
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {selectedNda.description}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* NDA Hash */}
                {selectedNda.ndaHash && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      NDA Hash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        padding: 1,
                        borderRadius: 1,
                      }}
                    >
                      {selectedNda.ndaHash}
                    </Typography>
                  </Box>
                )}

                {/* Blockchain Transaction Hash */}
                {selectedNda.blockchainTxHash ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Blockchain Transaction Hash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        padding: 1,
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      {selectedNda.blockchainTxHash}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={`https://polygonscan.com/tx/${selectedNda.blockchainTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver en PolygonScan
                    </Button>
                  </Box>
                ) : (
                  selectedNda.ndaHash &&
                  (isInvestigador() || isAdmin()) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Blockchain Transaction Hash:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        No registrado en blockchain
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={
                          registeringNdaId === selectedNda.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <UploadIcon />
                          )
                        }
                        onClick={async () => {
                          if (registeringNdaId) return; // Evitar múltiples clics
                          try {
                            setError("");
                            setSuccess("");
                            setRegisteringNdaId(selectedNda.id);
                            await ndaService.registerBlockchain(selectedNda.id);
                            setSuccess(
                              "NDA registrado en blockchain exitosamente"
                            );
                            await loadNdas();
                            // Actualizar el NDA seleccionado para mostrar el nuevo hash
                            const updatedNdas = await ndaService.getAll();
                            const updatedNda = updatedNdas.data.find(
                              (n) => n.id === selectedNda.id
                            );
                            if (updatedNda) {
                              setSelectedNda(updatedNda);
                            }
                          } catch (err) {
                            setError(
                              err.response?.data?.error ||
                                "Error al registrar NDA en blockchain"
                            );
                          } finally {
                            setRegisteringNdaId(null);
                          }
                        }}
                        disabled={!!registeringNdaId}
                        sx={{
                          backgroundColor: "#9c27b0", // Violeta
                          "&:hover": {
                            backgroundColor: "#7b1fa2", // Violeta más oscuro al hover
                          },
                        }}
                      >
                        {registeringNdaId === selectedNda.id
                          ? "Registrando..."
                          : "Registrar en Blockchain"}
                      </Button>
                    </Box>
                  )
                )}

                <Divider sx={{ my: 2 }} />

                {/* Archivo */}
                {selectedNda.filePath && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Archivo:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {selectedNda.filePath}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        href={buildFileUrl(selectedNda.filePath, "ndas")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver Archivo
                      </Button>
                    </Box>
                  </Box>
                )}

                {!selectedNda.filePath && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      No hay archivo asociado
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenInfoDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default NDAManagement;
